import { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Question from "./question";
import Input from "../../../../../components/input";
import Button from "../../../../../components/button";
import { v4 as uuidv4 } from "uuid";
import {
  updatePortalSurvey,
  createPortalSurvey,
  addSurveyAssignment,
  loadProfile,
} from "../../../../../api";
import { DashboardContext } from "../../../dashboard-context";
import { AuthContext } from "../../../../../auth-context";
import { useParams } from "react-router-dom";
import { SelectUsers } from "../../campaign/select-users";
import AssignClientsModal from "./assign-clients-modal";
import { withRouter } from "react-router-dom";
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1100px;
  width: 100%;
  /* gap: 10px; */
`;

const Container = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  margin: 12px 0px;
`;

const TextInput = styled.input`
  background: none;
  border: none;
  font-size: ${({ size }) => (size === "large" ? "1.5rem" : "1rem")};
  font-weight: 600;
  padding: 4px;
  max-width: 600px;
  width: 100%;
`;

const TextArea = styled.textarea`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
  /* max-width: 600px; */
  width: 100%;
  font-weight: 500;
  height: 200px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

const ButtonContainer = styled.div`
  max-width: 300px;
`;

const AddQuestion = styled.button`
  background: ${(props) =>
    props.warning ? "#f44336" : props.theme.button.background};
  border: none;
  border-radius: ${(props) => props.theme.button.borderRadius};
  color: ${(props) => props.theme.button.color};
  cursor: pointer;
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  height: 44px;
  margin: 16px 0 0 0;
  padding: 12px 18px;
  position: relative;
  transition: 0.2s ease all;
  opacity: ${(props) => (props.disabled ? "0.6" : "1")};

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 900px) {
    font-size: 0.875rem;
    height: 40px;
  }
`;

const newForm = {
  id: null,
  name: "",
  published: 0,
  survey_data: {
    title: "",
    description: "",
    questions: [],
  },
};

const Form = ({ forms, create, history, fetchSurveys }) => {
  const { user, updateUser } = useContext(AuthContext);
  const { openSnackbar } = useContext(DashboardContext);
  const { id } = useParams();
  const loadFormID = Number(id);
  const [form, setForm] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [showClients, setShowClients] = useState(false);
  const [showAssignedClients, setShowAssignedClients] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (loadFormID) {
      const getForm = forms.find((f) => f.id === loadFormID);
      setForm(getForm);
    }
  }, [loadFormID, forms]);

  useEffect(() => {
    if (create) {
      setForm(newForm);
    }
  }, [create]);

  const updateFormData = (data) => {
    setForm({ ...form, survey_data: { ...form.survey_data, ...data } });
  };

  const handleAddQuestion = (question) => {
    const newForm = { ...form };
    newForm.survey_data.questions.push(question);
    setForm(newForm);
    setSelectedQuestion(question.id);
  };

  const saveForm = () => {
    if (!validateForm()) {
      return;
    }

    if (create) {
      createPortalSurvey(user.id, form)
        .then(() => {
          fetchSurveys();
          history.push(`/dashboard/forms/view`);
          openSnackbar("Check-in form created successfully.", false);
        })
        .catch((err) => {
          openSnackbar(
            "There was a problem updating your check-in form.",
            true
          );
        });
    } else {
      updatePortalSurvey(user.id, form.id, form)
        .then(() => {
          fetchSurveys();
          history.push(`/dashboard/forms/view`);
          openSnackbar("Check-in form updated successfully.", false);
        })
        .catch((err) => {
          openSnackbar(
            "There was a problem updating your check-in form.",
            true
          );
        });
    }
  };

  const handleAssignSurvey = (selected) => {
    addSurveyAssignment(user.id, form.id, { account_ids: selected })
      .then(() => {
        openSnackbar("Check-in form assigned successfully.", false);
        loadProfile(user.id).then((res) => {
          updateUser(res.data.profile);
        });
      })
      .catch((err) => {
        openSnackbar("There was a problem assigning your check-in form.", true);
      });
  };

  const validateForm = () => {
    const questions = form.survey_data.questions || [];

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      if (
        (question.responseType === "multiple_choice" ||
          question.responseType === "checkbox") &&
        (!question.options || question.options.length === 0)
      ) {
        openSnackbar(
          `Question ${i + 1} (${
            question.responseType === "multiple_choice"
              ? "Multiple Choice"
              : "Checkbox"
          }) must have at least one option.`,
          true
        );
        return false;
      }
    }

    return true;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = form.survey_data.questions.findIndex(
        (q) => q.id === active.id
      );
      const newIndex = form.survey_data.questions.findIndex(
        (q) => q.id === over.id
      );

      const newForm = { ...form };
      newForm.survey_data.questions = arrayMove(
        form.survey_data.questions,
        oldIndex,
        newIndex
      );
      setForm(newForm);
    }
  };

  // Clients should only be assigned to one survey at a time
  const currentSelected = user.accounts
    .filter((account) => account.survey?.assigned_survey_id)
    .map((account) => {
      return {
        id: account.id,
        name: account.name,
        email_address: account.email_address,
      };
    });

  if (!form) return null;
  return (
    <FormContainer>
      <Input
        error={false}
        label="Form Name"
        value={form.name}
        placeholder="Enter a form name"
        onChange={(val) => setForm({ ...form, name: val })}
        info="Only displayed on the portal. Clients will not see this."
      />
      <Actions>
        <ButtonContainer>
          <Button label="Save changes" onClick={saveForm} />
        </ButtonContainer>
        {!create && (
          <>
            <ButtonContainer>
              <Button
                label="Assign client(s) to check-in form"
                onClick={() => setShowClients(true)}
              />
              <SelectUsers
                heading="Select clients to be assigned this check-in form"
                currentSelected={currentSelected}
                submit={(selected) => handleAssignSurvey(selected)}
                open={showClients}
                onClose={() => setShowClients(false)}
              />
            </ButtonContainer>
            <ButtonContainer>
              <Button
                label="View assigned clients"
                onClick={() => setShowAssignedClients(true)}
              />
              <AssignClientsModal
                open={showAssignedClients}
                onClose={() => setShowAssignedClients(false)}
                surveyID={form.id}
              />
            </ButtonContainer>
          </>
        )}
      </Actions>
      <Container>
        <TextInput
          error={false}
          placeholder="Untitled Form"
          value={form.survey_data.title}
          onChange={(e) => updateFormData({ title: e.target.value })}
          size="large"
        />
        <TextArea
          error={false}
          placeholder="Enter a description (optional)"
          value={form.survey_data.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
        />
      </Container>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={form?.survey_data?.questions?.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          {form?.survey_data?.questions?.map((question) => (
            <Question
              key={question.id}
              question={question}
              selected={selectedQuestion === question.id}
              setSelected={setSelectedQuestion}
              form={form}
              setForm={setForm}
            />
          ))}
        </SortableContext>
      </DndContext>
      <AddQuestion
        label="Add Question"
        onClick={() =>
          handleAddQuestion({
            id: uuidv4(),
            title: "",
            description: "",
            responseType: "multiple_choice",
            required: false,
            options: [],
          })
        }
      >
        Add Question
      </AddQuestion>
    </FormContainer>
  );
};

export default withRouter(Form);
